<template>
  <div class="video-wrapper" :class="{ 'user-inactive': !userActive }" id="videoWrapper" @click.stop>
    <!-- Video Cover -->
    <div class="video-cover" v-if="showCover" @click="startFromCover" :style="{ opacity: coverOpacity }">
      <img :src="`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`" alt="Video Cover" />
      <button @click.stop="startFromCover">
        <svg width="60" height="60" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M170 96c-24 0-42 18-42 42v236c0 24 18 42 42 42 9 0 18-3 25-8l196-118c21-13 21-37 0-50L195 104c-7-5-16-8-25-8z"
                stroke="white" stroke-width="32" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Loading overlay -->
    <div class="loading-overlay" v-show="loading">
      <div class="loading-spinner"></div>
    </div>

    <!-- Iframe -->
    <iframe
        id="player"
        ref="playerEl"
        :src="embedSrc"
        allow="autoplay; encrypted-media"
        allowfullscreen
        frameborder="0"
        @load="onIframeLoad"
    ></iframe>

    <!-- Pause overlay -->
    <div class="pause-overlay" :class="{ show: !isPlaying }" @click.stop="playVideo">
      <img :src="`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`" class="pause-overlay-cover" alt="Video Cover" />
      <button @click.stop="playVideo">
        <svg width="120" height="120" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M170 96c-24 0-42 18-42 42v236c0 24 18 42 42 42 9 0 18-3 25-8l196-118c21-13 21-37 0-50L195 104c-7-5-16-8-25-8z"
                stroke="white" stroke-width="32" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Skip indicators -->
    <div class="skip-indicator skip-left" :class="{ show: showBack && userActive && !hideUI }">-10s</div>
    <div class="skip-indicator skip-right" :class="{ show: showForward && userActive && !hideUI }">+10s</div>

    <!-- Center hover icons -->
    <div class="hover-controls" :class="{ visible: isPlaying && userActive && !hideUI }">
      <div class="hover-icon" @click.stop="skip(-10)">
        <svg width="80" height="80" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g fill="#FFFFFF">
            <path d="M128 28c55 0 100 45 100 100h-16c0-46.4-37.6-84-84-84-21.9 0-41.9 8.2-57.2 21.7l12 12c2.5 2.5 1.1 6.8-2.4 7.5l-41.9 8.6c-3.3.7-6.2-2.2-5.5-5.5l8.6-41.9c.7-3.5 5-4.9 7.5-2.4l11.9 11.9C78.6 37.8 102.2 28 128 28z"/>
            <path d="M128 228c-55 0-100-45-100-100h16c0 46.4 37.6 84 84 84s84-37.6 84-84h16c0 55-45 100-100 100z"/>
            <path d="M104 154v-52l-16 10" stroke="white" stroke-width="14" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="128" y="100" width="36" height="58" rx="18" fill="#ffffff00" stroke="white" stroke-width="14"/>
          </g>
        </svg>
      </div>

      <div class="hover-icon" @click.stop="togglePlay">
        <svg width="100" height="100" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="210" y="176" width="36" height="160" rx="18" fill="white"/>
          <rect x="306" y="176" width="36" height="160" rx="18" fill="white"/>
        </svg>
      </div>

      <div class="hover-icon" @click.stop="skip(10)">
        <svg width="80" height="80" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g fill="#FFFFFF">
            <path d="M128 28c-55 0-100 45-100 100h16c0-46.4 37.6-84 84-84 21.9 0 41.9 8.2 57.2 21.7l-12 12c-2.5 2.5-1.1 6.8 2.4 7.5l41.9 8.6c3.3.7 6.2-2.2 5.5-5.5l-8.6-41.9c-.7-3.5-5-4.9-7.5-2.4l-11.9 11.9C177.4 37.8 153.8 28 128 28z"/>
            <path d="M128 228c55 0 100-45 100-100h-16c0 46.4-37.6 84-84 84s-84-37.6-84-84H28c0 55 45 100 100 100z"/>
            <path d="M104 154v-52l-16 10" stroke="white" stroke-width="14" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="128" y="100" width="36" height="58" rx="18" fill="#ffffff00" stroke="white" stroke-width="14"/>
          </g>
        </svg>
      </div>
    </div>

    <!-- Bottom controls -->
    <div class="overlay" :class="{ hidden: !userActive || hideUI }" @mousemove.stop="resetInactivity">
      <div class="controls">
        <button @click.stop="togglePlay">{{ isPlaying ? '⏸️' : '▶️' }}</button>
        <input
            type="range"
            min="0"
            max="100"
            step="0.1"
            v-model.number="progressValue"
            @input="onProgressInput"
            :style="progressStyle"
        />
        <div class="time-display">{{ currentTimeText }} / {{ durationText }}</div>
        <select v-model.number="playbackRate" @change="onRateChange">
          <option :value="0.5">0.5x</option>
          <option :value="1">1x</option>
          <option :value="1.5">1.5x</option>
          <option :value="2">2x</option>
        </select>
        <button @click="toggleMute">{{ isMuted ? '🔇' : '🔊' }}</button>
        <button @click="enterFullscreen">⛶</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Custom-youTube-player',
  data() {
    return {
      // State
      player: null,
      isPlaying: false,
      isMuted: false,
      duration: 0,
      progressValue: 0,
      playbackRate: 1,
      // UI flags
      showBack: false,
      showForward: false,
      loading: false,
      // Cover
      showCover: true,
      coverOpacity: 1,
      // internals
      intervalId: null,
      hideBackTimeoutId: null,
      hideFwdTimeoutId: null,
      currentTime: 0,
      hideUI: false,
      userActive: true,
      inactivityTimerId: null,
      lastTimeUpdate: 0,
      //videoId is query from route
      videoId: '',
    };
  },
  computed: {
    embedSrc() {
      return `https://www.youtube.com/embed/${this.videoId}?enablejsapi=1&modestbranding=1&rel=0&controls=0&showinfo=0&iv_load_policy=3&disablekb=1`;
    },
    currentTimeText() {
      return this.formatTime(this.currentTime);
    },
    durationText() {
      return this.formatTime(this.duration);
    },
    progressStyle() {
      const v = this.progressValue || 0;
      return {
        background: `linear-gradient(to right, #00adee 0%, #00adee ${v}%, rgba(255,255,255,0.4) ${v}%, rgba(255,255,255,0.4) 100%)`
      };
    }
  },
  mounted() {
    const route = this.$route;
    this.videoId = route.query.videoId || '';

    // Load YT API
    if (!window.YT || !window.YT.Player) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = this.createPlayer;
    } else {
      this.createPlayer();
    }

    // Keyboard + double click
    window.addEventListener('keydown', this.onKey);
    const wrapper = this.$el;
    wrapper.addEventListener('dblclick', this.onDblClick);

    // Mouse movement tracking
    const reset = this.resetInactivity;
    document.addEventListener('mousemove', reset, { passive: true });
    document.addEventListener('mousedown', reset, { passive: true });
    document.addEventListener('keydown', reset);
    document.addEventListener('touchstart', reset, { passive: true });

    // Fullscreen lifecycle
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.addEventListener('msfullscreenchange', this.onFullscreenChange);

    // Start inactivity timer
    this.armInactivity();
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey);
    const wrapper = this.$el;
    wrapper.removeEventListener('dblclick', this.onDblClick);

    const reset = this.resetInactivity;
    document.removeEventListener('mousemove', reset);
    document.removeEventListener('mousedown', reset);
    document.removeEventListener('keydown', reset);
    document.removeEventListener('touchstart', reset);

    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('msfullscreenchange', this.onFullscreenChange);

    clearTimeout(this.inactivityTimerId);
    clearInterval(this.intervalId);
  },

  methods: {
    createPlayer() {
      this.player = new window.YT.Player('player', {
        events: {
          onReady: this.onPlayerReady,
          onStateChange: this.onPlayerStateChange,
          onError: this.onPlayerError
        }
      });
    },

    onIframeLoad() {
      // Keep for any iframe load side-effects if needed
    },

    onPlayerReady() {
      this.duration = this.player.getDuration ? (this.player.getDuration() || 0) : 0;

      // ✅ ضبط الجودة لأعلى مستوى متاح
      try {
        const qualities = this.player.getAvailableQualityLevels();
        if (qualities && qualities.length > 0) {
          this.player.setPlaybackQuality(qualities[0]); // أول عنصر غالباً هو الأعلى (hd1080 أو hd720 أو highres)
        }

            // ✅ ننتظر شوية وبعدين نطبع الجودة الفعلية
    setTimeout(() => {
      this.player.getPlaybackQuality();
    }, 5000); // انتظر 1 ثانية

      } catch (err) {
        console.warn('Quality setup failed:', err);
      }

      if (this.intervalId) clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        if (!this.player) return;
        const total = this.player.getDuration ? this.player.getDuration() : 0;
        if (total > 0) this.duration = total;
        if (this.isPlaying) {
          const current = this.player.getCurrentTime ? this.player.getCurrentTime() : 0;
          this.currentTime = current;
          this.lastTimeUpdate = current;
          const pct = total > 0 ? (current / total) * 100 : 0;
          this.progressValue = pct || 0;
        }
      }, 500);
    },

    onPlayerStateChange(event) {
      const YTS = window.YT.PlayerState;
      if (event.data === YTS.PLAYING) {
        this.isPlaying = true;
      } else if (event.data === YTS.PAUSED || event.data === YTS.ENDED) {
        this.isPlaying = false;
      }
    },

    onPlayerError() {
      this.isPlaying = false;
    },

    formatTime(s) {
      s = Math.floor(s || 0);
      const m = Math.floor(s / 60);
      const r = (s % 60).toString().padStart(2, '0');
      return `${m}:${r}`;
    },

    playVideo() {
      if (!this.player) return;
      try { 
        this.player.playVideo && this.player.playVideo(); 
        // ✅ بعد التشغيل مباشرة نرجع أعلى جودة
        const qualities = this.player.getAvailableQualityLevels();
        if (qualities && qualities.length > 0) {
          this.player.setPlaybackQuality(qualities[0]);
        }

            // ✅ ننتظر شوية وبعدين نطبع الجودة الفعلية
    setTimeout(() => {
      this.player.getPlaybackQuality();
    }, 5000); // انتظر 1 ثانية

      } catch {
        console.warn('Play error:');
      }
    },

    pauseVideo() {
      if (!this.player) return;

      // أظهر الـ pause overlay فورًا
      this.isPlaying = false;

      // أوقف الفيديو بعد 300 مللي ثانية
      setTimeout(() => {
        try {
          this.player.pauseVideo && this.player.pauseVideo();
        } catch {
          console.warn('pause error:');
        }
      }, 300);
    },

    togglePlay() {
      if (this.isPlaying) this.pauseVideo();
      else this.playVideo();
    },

    skip(seconds) {
      if (!this.player || !this.player.getCurrentTime) return;
      const current = this.player.getCurrentTime();
      const newTime = Math.max(0, Math.min(current + seconds, this.duration || current + seconds));
      this.player.seekTo && this.player.seekTo(newTime, true);

      // Indicators
      if (seconds < 0) {
        this.showBack = true;
        clearTimeout(this.hideBackTimeoutId);
        this.hideBackTimeoutId = setTimeout(() => (this.showBack = false), 600);
      } else {
        this.showForward = true;
        clearTimeout(this.hideFwdTimeoutId);
        this.hideFwdTimeoutId = setTimeout(() => (this.showForward = false), 600);
      }
    },

    onProgressInput(e) {
      if (!this.player || !this.duration) return;
      const pct = Number(e.target.value || 0);
      const newTime = (pct / 100) * this.duration;
      this.player.seekTo && this.player.seekTo(newTime, true);
    },

    onRateChange() {
      if (!this.player) return;
      try { this.player.setPlaybackRate && this.player.setPlaybackRate(this.playbackRate); } catch {
        console.warn('rate change error:');
      }
    },

    toggleMute() {
      if (!this.player) return;
      if (this.isMuted) {
        this.player.unMute && this.player.unMute();
      } else {
        this.player.mute && this.player.mute();
      }
      this.isMuted = !this.isMuted;
    },

    enterFullscreen() {
      const iframe = this.$refs.playerEl;

      // لو الآيفون، استخدم محاكاة fullscreen (CSS)
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isIOS) {
        // simulate fullscreen for iPhone
        const wrapper = this.$el;
        wrapper.classList.add("ios-fullscreen");
        document.body.style.overflow = "hidden";

        const exit = () => {
          wrapper.classList.remove("ios-fullscreen");
          document.body.style.overflow = "";
          document.removeEventListener("click", exit);
        };

        // المستخدم يقدر يخرج بالضغط على الشاشة
        document.addEventListener("click", exit);
        return;
      }

      // Desktop / Android normal fullscreen
      if (iframe.requestFullscreen) iframe.requestFullscreen();
      else if (iframe.webkitRequestFullscreen) iframe.webkitRequestFullscreen();
      else if (iframe.msRequestFullscreen) iframe.msRequestFullscreen();
    },

    startFromCover() {
      this.coverOpacity = 0;
      setTimeout(() => {
        this.showCover = false;
        this.playVideo();
      }, 500);
    },

    onDblClick(e) {
      const target = e.target;
      const isControl = target.closest && (
          target.closest('.controls') ||
          target.closest('.pause-overlay') ||
          target.closest('.loading-overlay')
      );
      if (isControl) return;

      const rect = this.$el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (x < rect.width / 2) this.skip(-10);
      else this.skip(10);
    },

    onKey(e) {
      if (!this.player) return;
      if (e.key === 'ArrowRight') {
        this.skip(10);
        this.showForward = true;
        clearTimeout(this.hideFwdTimeoutId);
        this.hideFwdTimeoutId = setTimeout(() => (this.showForward = false), 600);
      } else if (e.key === 'ArrowLeft') {
        this.skip(-10);
        this.showBack = true;
        clearTimeout(this.hideBackTimeoutId);
        this.hideBackTimeoutId = setTimeout(() => (this.showBack = false), 600);
      } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        this.togglePlay();
      }
    },

    armInactivity() {
      clearTimeout(this.inactivityTimerId);

      // Auto-hide after 2 seconds in both fullscreen and normal mode
      this.inactivityTimerId = setTimeout(() => {
        this.userActive = false;
        this.hideUI = true;
      }, 2000);
    },

    resetInactivity() {
      this.userActive = true;
      this.hideUI = false;
      this.armInactivity();
    },

    onFullscreenChange() {
      // Reset UI state when fullscreen changes
      this.userActive = true;
      this.hideUI = false;
      clearTimeout(this.inactivityTimerId);

      // Start 2-second inactivity timer
      this.inactivityTimerId = setTimeout(() => {
        this.userActive = false;
        this.hideUI = true;
      }, 2000);
    },
  }
};
</script>

<style scoped>
</style>
